<script>
/* eslint-disable import/order */ /* <= reason: порядок импорта очень важен в этом месте  */
import { defineComponent } from 'vue';
import { mask } from 'vue-the-mask';

export default defineComponent({
  directives: {
    mask,
  },
});
</script>

<script setup>
  import { ref, watch, computed } from 'vue';
  import { isNumber, debounce } from 'lodash-es';
  import { useI18n } from 'vue-i18n';
  import { useError } from '@/composables/useError';

  const props = defineProps({
    modelValue: {
      type: [String, Number],
      default: () => '',
    },
    label: {
      type: String,
      default: () => '',
    },
   placeholder: {
     type: [String, Number],
     default: () => '',
   },
  autosize: {
     type: Boolean,
     default: () => false,
   },
   disabled: {
     type: Boolean,
     default: () => false,
   },
   required: {
     type: Boolean,
     default: () => false,
   },
   size: {
     type: String,
     default: () => 'default',
   },
   labelSize: {
     type: Number,
     default: () => 14,
   },
   error: {
     type: [String, Number, Array],
     default: () => '',
   },
   errorTooltip: {
     type: Boolean,
     default: () => false,
   },
   isError: {
     type: Boolean,
     default: () => false,
   },
    clearable: {
      type: Boolean,
      default: () => false,
    },
    isSearch: {
      type: Boolean,
      default: () => false,
    },
    searchTimer: {
      type: Number,
      default: () => 500,
    },
    width: {
      type: [String, Number],
      default: () => null,
    },
    preIcon: {
      type: String,
      default: () => '',
    },
    postIcon: {
      type: String,
      default: () => '',
    },
    postText: {
      type: String,
      default: () => '',
    },
    tabindex: {
      type: Number,
      default: () => 1,
    },
    readonly: {
      type: Boolean,
      default: () => false,
    },
    borderless: {
      type: Boolean,
      default: () => false,
    },
    hint: {
      type: String,
      default: () => '',
    },
    hintIsHtml: {
      type: Boolean,
      default: () => false,
    },
    type: {
      type: String,
      default: () => 'text',
    },
    inputMask: {
      type: String,
      default: () => '',
    },
  });

  const emit = defineEmits(['blur', 'error', 'focus', 'change', 'update:modelValue', 'clear', 'init', 'search', 'click-post']);

//#region data
  const { t } = useI18n();
  const hovered = ref(false);
  const focused = ref(false);
  const currentValue = ref('');
  const inputRef = ref(null);

  const { currentError } = useError({
    error: computed(() => {
      return props.error;
    }),
  });
//#endregion

//#region computed
  const getInputWidth = computed(() => {
    if (props.autosize) return '100%';

    if (props.width) {
      return isNumber(props.width)
        ? `${props.width}px`
        : props.width;
    }
    return '';
    //return props.size === 'big' ? '280px' : '180px';
  });
  const getIconSize = computed(() => {
    return props.size === 'big' ? 16 : 12;
  });

  const previousIcon = computed(() => {
    return props.preIcon ? props.preIcon : props.isSearch ? 'search' : '';
  });

  const pPlaceholder = computed(() => {
    return props.isSearch ? t('ui.input.search') : props.placeholder;
  });

  const showClear = computed(() => {
    return (
      (props.clearable || props.isSearch)
      && !props.disabled
      && !props.readonly
      && currentValue.value !== ''
      && (focused.value || hovered.value)
    );
  });

  const isPasswordVisible = ref(false);

  const resolvedType = computed(() => {
    if (props.type === 'password-text') {
      return isPasswordVisible.value ? 'text' : 'password';
    }

    return props.type || 'text';
  });

  const resolvedPostIcon = computed(() => {
    if (props.type === 'password-text') {
      return isPasswordVisible.value ? 'eye' : 'eye-slash';
    }

    return props.postIcon;
  });
//#endregion


  //#region watchers
  watch(() => props.modelValue, async (newValue, oldValue) => {
    if (newValue !== oldValue) {
      currentValue.value = newValue === undefined || newValue === null ? '' : newValue.toString();
    }
  }, { deep: true, immediate: true });

  watch(() => currentValue.value, async (newValue, oldValue) => {
    if (newValue !== oldValue && props.isSearch) {
      debouncedSearch();
    }
  });

//#endregion

//#region methods
  const debouncedSearch = debounce(() => {
    emit('search', currentValue.value);
  }, props.searchTimer);

  const handleInput = (event) => {
    const { value } = event.target;
    currentValue.value = value;
    emit('update:modelValue', value);
  };

  const handleFocus = (event) => {
    focused.value = true;
    emit('focus', event);
  };

  const handleBlur = (event) => {
    focused.value = false;
    if (props.required && !event.target.value) {
      emit('error', currentError.value);
    }
    emit('blur', event);
  };

  const focus = () => {
    inputRef.value?.focus();
  };

  const blur = () => {
    inputRef.value?.blur();
  };

  const handleChange = (event) => {
    const { value } = event.target;
    emit('change', value);
  };

  const handleKeydown = (event) => {
    emit('change', event);
  };

  const clear = () => {
    emit('update:modelValue', '');
    emit('change', '');
    emit('clear');
    currentValue.value = '';
    focus();
  };

  const handleClickPost = () => {
    if (props.disabled) return;

    if (props.type === 'password-text') {
      isPasswordVisible.value = !isPasswordVisible.value;
    }

    emit('click-post');
  };

  //#endregion

defineExpose({
  focus,
  blur,
});

</script>

<template>
  <label
    :class="[
      'ui-input',
      `ui-input_${size}`,
      {
        'ui-input_focused': focused,
        'ui-input_error': currentError || isError,
        'ui-input_disabled': disabled,
        'ui-input_readonly': readonly,
        'ui-input_borderless': borderless,
      }
    ]"
    :style="{ width: getInputWidth }"
    @mouseover="hovered = true"
    @mouseleave="hovered = false"
  >
    <UiLabel
      v-if="label"
      :font-size="labelSize"
      :label="label"
      :hint="hint"
      :hint-is-html="hintIsHtml"
      :required="required"
      :disabled="disabled"
      class="t:mb-8"
    />


    <ElTooltip
      placement="top"
      :content="currentError"
      :disabled="!(currentError && errorTooltip && !focused)"
    >
      <div class="ui-input__wrapper">
        <UiIcon
          v-if="previousIcon !== ''"
          :name="previousIcon"
          :size="getIconSize"
          color="var(--text-body-secondary)"
          class="t:mr-4 t:self-center"
        />
        <input
          v-if="inputMask"
          ref="inputRef"
          v-mask="inputMask"
          :tabindex="tabindex"
          :placeholder="placeholder"
          :readonly="readonly"
          :type="resolvedType"
          :disabled="disabled"
          :value="currentValue"
          class="ui-input__element"
          @input="handleInput"
          @focus="handleFocus"
          @blur="handleBlur"
          @change="handleChange"
        >

        <input
          v-else
          ref="inputRef"
          :tabindex="tabindex"
          :placeholder="pPlaceholder"
          :readonly="readonly"
          :type="resolvedType"
          :disabled="disabled"
          :value="currentValue"
          class="ui-input__element"
          @input="handleInput"
          @focus="handleFocus"
          @blur="handleBlur"
          @change="handleChange"
          @keydown="handleKeydown"
        >
        <ActionIcon
          v-if="(clearable || isSearch) && showClear"
          icon="times-circle"
          color="var(--text-body-secondary)"
          custom-class="t:ml-8"
          :size="14"
          :is-overlay="false"
          @click="clear"
        />
        <span
          v-if="postIcon || postText || type === 'password-text'"
          class="ui-input__append"
        >
          <ActionIcon
            v-if="postIcon || type === 'password-text'"
            :icon="resolvedPostIcon"
            :class="{ 't:cursor-pointer': type === 'password-text' }"
            :is-overlay="false"
            color="var(--text-body-secondary)"
            @click="handleClickPost"
          />
          <span
            v-if="postText"
            class="ui-input__append-content"
            @click="!disabled && emit('click-post')"
          >
            {{ postText }}
          </span>
        </span>
      </div>
    </ElTooltip>
    <UiHelp
      v-if="currentError && !errorTooltip"
      :is-error="true"
      :text="currentError"
    />
  </label>
</template>

<style lang="scss">
.ui-input {
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease 0s;
  box-sizing: border-box;
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &__wrapper {
    display: inline-flex;
    align-items: stretch;
    border-radius: 5px;
    border: 1px solid var(--border-default);
    background-color: var(--background-body);
    transition: border-color 0.2s;
    overflow: hidden;
    padding: 0 12px;
  }
  &__element {
    display: flex;
    align-items: center;
    height: 100%;
    min-width: 10%;
    max-width: 100%;
    flex: 10 1 auto;

    box-sizing: border-box;
    border: none;
    outline: none;
    background: transparent;
    font-weight: 400;
    line-height: normal;
    color: var(--text-body);
    &::placeholder {
      font-family: var(--main-font);
    }
  }

  &__append{
    display: flex;
    border-left: 1px solid var(--border-default);
    padding-left: 12px;
    margin-left: 8px;
    color: var(--text-body);
  }
  &__append-content {
    display: flex;
    align-items: center;
  }
  &_big {
    width: 280px;
  }
  &_default {
    width: 180px;
  }
  &_big & {
    &__wrapper {
      height: 34px;
      input {
        font-size: 13px;
      }
    }
    &__append {
      font-size: 13px;
    }
  }
  &_default & {
    &__wrapper {
      height: 30px;
      input {
        font-size: 12px;
      }
    }
    &__append {
      font-size: 12px;
    }
  }
  &_error & {
    &__wrapper {
      border-color: var(--border-negative);
    }
  }
  &_focused & {
    &__wrapper {
      border-color: var(--border-focused);
    }
  }
  &_disabled & {
    &__wrapper {
      opacity: 0.5;
      pointer-events: none;
    }
  }

  &_readonly &{
    cursor: not-allowed;
    &__wrapper,
    &__element {
      cursor: not-allowed;
    }
  }
  &_borderless &{
    &__wrapper {
      border: none;
    }
  }
}
</style>
